import { call, put, select, takeLatest } from 'redux-saga/effects'
import { handleApiError } from '../../../components/apiErrorHandler/store/apiErrorHandler.actions'
import { getAuthenticationJwtSelector } from '../../login/selectors/getAuthenticationJwt.selector'
import { PulsaraConstants } from '../store/pulsara.constants'
import { PulsaraService } from '../services/pulsara.service'
import { IPulsaraRecordsRequest, IPulsaraRecordsResponse } from '../store/pulsara.types'
import i18n from '../../../i18n'
import { setLoading, setPulsaraRecords } from '../store/pulsara.actions'
import { performWithMutexHOG } from '../../../services/mutexUtility.service'
import { isOffline } from '../../../utilities'
import { setErrorMessage, showToast } from '../../../components/apiErrorHandler/store/apiErrorHandler.actions'
import Logger from '../../../services/logger.service'
import { IDateRangeOption } from '../../../common/common.types'

export function* fetchPulsaraRecords(action: { selectedDateRange: IDateRangeOption }) {
  const { hours } = action.selectedDateRange
  if (isOffline()) {
    yield put(setErrorMessage(i18n.t('pulsara.errors.offline')))
    yield put(showToast(true))
    return
  }

  const authenticationJwt: string = yield select(getAuthenticationJwtSelector)

  const nowDate = new Date()
  const endDateUtc = nowDate.toISOString()
  const startDateUtc = new Date(nowDate.getTime() - hours * 60 * 60 * 1000).toISOString()

  yield put(setLoading('records'))

  try {
    const pulsaraService = new PulsaraService(authenticationJwt)

    const request: IPulsaraRecordsRequest = {
      startDate: startDateUtc,
      endDate: endDateUtc,
    }

    const response: IPulsaraRecordsResponse = yield call([pulsaraService, pulsaraService.fetchPulsaraRecords], request)

    if (response.status === 200) {
      const pulsaraRecords = response.data.patientChannels

      yield put(setPulsaraRecords(pulsaraRecords))
      Logger.info(`Pulsara: fetchPulsaraRecords success, records count: ${pulsaraRecords?.length ?? 0}`)
    } else {
      const messageIndex = response.messages?.findIndex((x) => x.status === response.status) ?? -1
      const errorMessage = messageIndex >= 0 ? response.messages?.[messageIndex]?.message : undefined
      const isNoApiKeyError = response.status === 400 && errorMessage === 'Invalid or missing Pulsara API Key'

      if (isNoApiKeyError) {
        const errorResponse = {
          status: 400,
          messages: [
            {
              status: 400,
              display: [
                { id: 3, name: 'Alert' },
                { id: 1, name: 'Log' },
              ],
              message: i18n.t('pulsara.errors.invalidApiKey'),
            },
          ],
          callback: 'handleInvalidPulsaraApiKey',
        }
        yield put(handleApiError(errorResponse))
      } else {
        response.failure = true
        if (response.messages?.length) {
          response.responseMessage =
            messageIndex >= 0 ? response.messages[messageIndex]?.message : i18n.t('pulsara.errors.fetchRecords')
        } else {
          response.responseMessage = i18n.t('pulsara.errors.fetchRecords')
        }
        yield put(handleApiError(response))
      }
    }
  } catch (e: any) {
    const cause = e.message ? e.message : 'unknown'
    const errorResponse = {
      status: null,
      messages: [
        {
          status: null,
          display: [
            { id: 3, name: 'Alert' },
            { id: 1, name: 'Log' },
          ],
          message: i18n.t('pulsara.errors.fetchRecordsFailed', { cause }),
        },
      ],
    }
    yield put(handleApiError(errorResponse))
  } finally {
    yield put(setLoading(null))
  }
}

export function* fetchPulsaraRecordsSaga() {
  yield takeLatest([PulsaraConstants.FETCH_PULSARA_RECORDS], performWithMutexHOG, fetchPulsaraRecords)
}
